import React from 'react';
import { Link } from 'react-router-dom';
import './Privacy.css';
import Header from './Header';

const Privacy = () => {
  return (
    <div>
      {/* Floating background elements */}
      <div className="floating-elements">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      <Header />

      <div className="container">
        <div className="page-header">
          <div className="app-icon">
            <img src="croqly-logo.jpeg" alt="Croqly App Icon" />
          </div>
          <h1 className="page-title">Privacy Policy</h1>
          <p className="page-subtitle">Croqly – Crochet & Knitting Companion</p>
          <div className="last-updated">Effective Date: November 23, 2025<br />Last Updated: November 23, 2025</div>
        </div>

        <main className="content">
          <h2>Summary</h2>
          <p>Croqly collects only the information needed to operate your account, manage projects, and process in-app purchases.</p>
          <p>We never sell your data and share it only with trusted partners such as Apple, Google Firebase (anonymous authentication), and RevenueCat under strict confidentiality agreements.</p>
          <p>All personal data is stored locally on your device and is not transmitted to external servers unless required for authentication, subscriptions, or analytics you explicitly enable.</p>

          <h2>1. Scope</h2>
          <p>This Privacy Policy ("Policy") explains how Croqly ("we," "us," "our") collects, uses, and protects information when you use the Croqly iOS App ("App").</p>
          <p>By using the App, you agree to this Policy.</p>

          <h2>2. Information We Collect</h2>
          <p><strong>Account Data</strong></p>
          <ul>
            <li>Craft preferences (crochet/knitting), skill level, motivations, time commitment</li>
            <li>App settings, language, theme, notification preferences</li>
          </ul>
          <p><strong>Authentication Data</strong></p>
          <ul>
            <li>Firebase anonymous user identifier (random UID, no email/name required)</li>
            <li>Token metadata needed to keep you signed in securely</li>
          </ul>
          <p><strong>User-Generated Content</strong></p>
          <ul>
            <li>Project progress, notes, session logs</li>
            <li>Stash inventory (yarn, hook, and needle details)</li>
            <li>Saved patterns, tutorial progress, completion status</li>
          </ul>
          <p><strong>Usage Data</strong></p>
          <ul>
            <li>Tutorial progress, streak tracking, time spent crafting</li>
            <li>Feature interactions, session length</li>
            <li>Optional crash logs</li>
          </ul>
          <p><strong>Device Data</strong></p>
          <ul>
            <li>Device model, iOS version, language, region</li>
            <li>Local app database and performance logs (SQLite, on-device only)</li>
          </ul>
          <p><strong>Payment Data</strong></p>
          <ul>
            <li>Subscription receipt IDs and renewal status (via RevenueCat)</li>
            <li>Purchase history handled through Apple's App Store</li>
          </ul>
          <p>We do not collect personal identifiers (e.g., name, email) unless you contact us directly.</p>

          <h2>3. How We Use Your Information</h2>
          <p>We use collected data to:</p>
          <ul>
            <li>Operate and improve the App and its features</li>
            <li>Maintain your project and stash data between sessions</li>
            <li>Authenticate you anonymously via Firebase Authentication</li>
            <li>Process subscriptions via Apple / RevenueCat</li>
            <li>Provide optional notifications and streak reminders</li>
            <li>Diagnose crashes and improve reliability (if enabled)</li>
            <li>Comply with applicable laws and App Store policies</li>
          </ul>
          <p>We do not use your data for advertising or cross-app tracking.</p>

          <h2>3.1. Subscription Services</h2>
          <h3>Subscription Plans</h3>
          <p>Croqly offers the following subscription plans:</p>
          <ul>
            <li><strong>Weekly Premium Plan:</strong> $4.99 per week</li>
            <li><strong>Annual Premium Plan:</strong> $29.99 per year</li>
          </ul>
          
          <h3>Subscription Features</h3>
          <p>Premium subscribers have access to:</p>
          <ul>
            <li>Unlimited access to all crochet patterns and tutorials</li>
            <li>Step-by-step crochet and amigurumi tutorials</li>
            <li>Pattern organization and management tools</li>
            <li>Progress tracking and work session management</li>
            <li>Yarn, hooks, and needles stash organization</li>
            <li>Essential crochet utilities and calculators</li>
            <li>Ad-free experience</li>
            <li>Priority customer support</li>
          </ul>

          <h2>4. Legal Bases (EEA / UK Users)</h2>
          <p>Processing is based on:</p>
          <ul>
            <li><strong>Contract:</strong> To deliver core App functions</li>
            <li><strong>Legitimate Interest:</strong> Security, anonymous authentication, analytics, improvement</li>
            <li><strong>Consent:</strong> Optional notifications or analytics (which can be withdrawn anytime)</li>
          </ul>

          <h2>5. Sharing & Disclosure</h2>
          <p>We share data only when necessary:</p>
          <table style={{width: '100%', borderCollapse: 'collapse', margin: '1rem 0', color: '#F8F9FA'}}>
            <thead>
              <tr style={{borderBottom: '1px solid rgba(255, 107, 107, 0.3)'}}>
                <th style={{padding: '0.5rem', textAlign: 'left', color: '#F8F9FA'}}>Purpose</th>
                <th style={{padding: '0.5rem', textAlign: 'left', color: '#F8F9FA'}}>Partner</th>
                <th style={{padding: '0.5rem', textAlign: 'left', color: '#F8F9FA'}}>Data Handled</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{borderBottom: '1px solid rgba(255, 107, 107, 0.1)'}}>
                <td style={{padding: '0.5rem', color: '#F8F9FA'}}>In-App Purchases</td>
                <td style={{padding: '0.5rem', color: '#F8F9FA'}}>Apple Inc.</td>
                <td style={{padding: '0.5rem', color: '#F8F9FA'}}>Purchase receipts, subscription status</td>
              </tr>
              <tr style={{borderBottom: '1px solid rgba(255, 107, 107, 0.1)'}}>
                <td style={{padding: '0.5rem', color: '#F8F9FA'}}>Anonymous Authentication</td>
                <td style={{padding: '0.5rem', color: '#F8F9FA'}}>Google Firebase Authentication</td>
                <td style={{padding: '0.5rem', color: '#F8F9FA'}}>Random anonymous UID, token metadata</td>
              </tr>
              <tr>
                <td style={{padding: '0.5rem', color: '#F8F9FA'}}>Subscription Management</td>
                <td style={{padding: '0.5rem', color: '#F8F9FA'}}>RevenueCat Inc.</td>
                <td style={{padding: '0.5rem', color: '#F8F9FA'}}>Anonymous user ID, receipt verification</td>
              </tr>
            </tbody>
          </table>
          <p>We use no third-party advertising or analytics SDKs (e.g., no Google Analytics). Firebase is used strictly for anonymous authentication and does not grant advertisers access to your data.</p>
          <p>Data may also be disclosed:</p>
          <ul>
            <li>To comply with lawful requests</li>
            <li>During a merger or acquisition, under equivalent safeguards</li>
          </ul>
          <p>All partners must handle data in line with this Policy and data-protection laws.</p>

          <h2>6. Data Retention & Deletion</h2>
          <p>Data is stored only on your device while the App is installed.</p>
          <p>When you delete your account or uninstall the App, local data is permanently erased.</p>
          <p>The Firebase anonymous UID associated with your device is deleted or anonymized when you trigger the in-app data deletion flow or email us a removal request.</p>
          <p>Minimal purchase metadata may remain with Apple / RevenueCat as required for billing compliance.</p>
          <p><strong>How to Delete Data</strong></p>
          <ul>
            <li><strong>In-App:</strong> Settings › "Clear All Data" (immediate)</li>
            <li><strong>Email:</strong> <a href="mailto:itarnaevents2@gmail.com" className="contact-link">itarnaevents2@gmail.com</a></li>
            <li><strong>Uninstall:</strong> Removes all local data automatically</li>
          </ul>
          <p>We confirm deletion requests within 30 days.</p>

          <h2>7. Security</h2>
          <p>We use industry-standard safeguards:</p>
          <ul>
            <li>Encrypted local database (SQLite encryption)</li>
            <li>No external data transmission except to Apple / RevenueCat</li>
            <li>Device-level authentication and OS security</li>
          </ul>
          <p>Despite these measures, no system is 100 % secure; protect your device with a passcode or biometrics.</p>

          <h2>8. Your Privacy Rights</h2>
          <p>Depending on your region, you may:</p>
          <ul>
            <li>Access or export your data</li>
            <li>Correct inaccuracies</li>
            <li>Delete your account and data</li>
            <li>Withdraw consent for analytics or notifications</li>
          </ul>
          <p>Submit requests via the in-app Settings or email <a href="mailto:itarnaevents2@gmail.com" className="contact-link">itarnaevents2@gmail.com</a>.</p>
          <p>We may verify identity for your protection.</p>

          <h2>9. Children's Privacy</h2>
          <p>The App is not intended for children under 13.</p>
          <p>We do not knowingly collect information from minors.</p>
          <p>If such data is found, it will be deleted immediately upon notice.</p>

          <h2>10. International Transfers</h2>
          <p>Your data remains on-device.</p>
          <p>Limited payment-related data processed by Apple / RevenueCat may occur in the U.S. or EU, subject to standard contractual clauses and each provider's privacy terms.</p>

          <h2>11. California Privacy Rights (CCPA)</h2>
          <p>California residents may request:</p>
          <ul>
            <li>Disclosure of collected categories of personal information</li>
            <li>Deletion of stored information</li>
            <li>Confirmation that Croqly does not sell personal data</li>
          </ul>
          <p>Requests: <a href="mailto:itarnaevents2@gmail.com" className="contact-link">itarnaevents2@gmail.com</a> with subject "CCPA Request".</p>

          <h2>12. Data Collection Controls</h2>
          <p>You can manage collection anytime under Settings › Privacy & Data:</p>
          <ul>
            <li>Toggle analytics and crash reporting</li>
            <li>Enable/disable notifications</li>
            <li>Clear stored data</li>
          </ul>

          <h2>13. Third-Party Links & App Store Policies</h2>
          <p>Any purchases or subscriptions are processed through Apple's App Store.</p>
          <p>Apple Inc. is not responsible for Croqly's content or data-handling practices.</p>
          <p>Refer to Apple's own privacy policy for App Store transactions.</p>

          <h2>14. Changes to This Policy</h2>
          <p>We may update this Policy from time to time.</p>
          <p>Material changes will be announced in-app or by email at least 7 days before taking effect.</p>
          <p>The latest version is always available in App Settings.</p>

          <h2>15. Contact Us</h2>
          <p><strong>Croqly – Crochet & Knitting Companion</strong><br />
          Email: <a href="mailto:itarnaevents2@gmail.com" className="contact-link">itarnaevents2@gmail.com</a></p>
          <p>Apple Inc. is not responsible for the App's operation or customer support.<br />
          For all privacy inquiries, please contact Croqly directly.</p>
          <p>This Privacy Policy is effective November 23, 2025, and governs your use of the Croqly app.</p>
        </main>
      </div>

      <footer>
        <div className="container">
          <div className="footer-content">
            <div className="footer-links">
              <Link to="/">Home</Link>
              <Link to="/terms">Terms of Service</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div>
              <p>&copy; 2025 Croqly. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;